import { Remove } from '@mui/icons-material';
import {
  Button,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { Form, Formik, FieldArray } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import CustomButton from '../../elements/CustomButton';
import Dropdown2 from '../../elements/Dropdown2';
import InputField from '../../elements/InputField';
import { ProductSpecsOptions } from '../../../staticData/tableHead';
import FormattedTextArea from '../../elements/FormattedTextArea';

const ProductSpecsForm = ({ onSubmit, product }) => {
  const productTypes = product?.details?.map((detail) => detail.type) || [];

  const specCount = productTypes.filter((type) => type === 'spec').length;

  const availableTypes = ProductSpecsOptions.filter((option) => {
    if (option.value === 'spec') {
      return specCount < 2;
    } else {
      return !productTypes.includes(option.value);
    }
  });

  return (
    <Formik
      initialValues={{
        type: '',
        name: '',
        description: [''],
      }}
      validationSchema={Yup.object().shape({
        type: Yup.string().required('Type is required'),
        name: Yup.string().when(['type', 'description'], (type) => {
          return type === 'spec' || Yup.string().notRequired();
        }),
        description: Yup.array()
          .of(Yup.string())
          .required('Description is required'),
      })}
      onSubmit={onSubmit}>
      {({
        errors,
        handleBlur,
        handleSubmit,
        touched,
        values,
        handleChange,
      }) => (
        <Form noValidate onSubmit={handleSubmit}>
          {availableTypes.length > 0 ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <Dropdown2
                    elementName="type"
                    selected={values.type}
                    handleChange={handleChange}
                    elementLabel="Type"
                    options={availableTypes}
                    required
                  />
                </Stack>
              </Grid>
              {values.type === 'spec' && (
                <Grid item xs={12}>
                  <Stack spacing={1}>
                    <InputField
                      label="Name"
                      id="name"
                      type="text"
                      value={values.name}
                      name="name"
                      fullWidth
                      placeholder="Name"
                      error={Boolean(touched.name && errors.name)}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack spacing={1}>
                  <InputLabel>Description:</InputLabel>
                  <FieldArray
                    name="description"
                    render={(arrayHelpers) => (
                      <div>
                        {values.description.map((point, index) => (
                          <>
                            <FormattedTextArea
                              id={`description[${index}]`}
                              name={`description[${index}]`}
                              fullWidth
                              value={point}
                              placeholder="Enter description"
                              onChange={handleChange}
                            />
                            {values.type && values.type !== 'spec' && (
                              <IconButton
                                onClick={() => arrayHelpers.remove(index)}
                                color="secondary"
                                sx={{paddingBottom: 0}}
                                title='Remove point'
                                disabled={index === 0}>
                                <Remove />
                              </IconButton>
                            )}
                          </>
                        ))}
                        {values.type && values.type !== 'spec' && (
                          <Stack
                            direction="row"
                            justifyContent="center"
                            spacing={2}>
                            <Button
                              onClick={() => arrayHelpers.push('')}
                              color="primary"
                              variant="text"
                              sx={{ mt: '10px' }}>
                              Add row
                            </Button>
                          </Stack>
                        )}
                      </div>
                    )}
                  />
                </Stack>
              </Grid>
              {errors.submit && (
                <Grid item xs={12}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Grid>
              )}
              <Grid item xs={12}>
                <CustomButton />
              </Grid>
            </Grid>
          ) : (
            <Typography
              sx={{ textAlign: 'center' }}
              variant="caption"
              component="p">
              All product details added
            </Typography>
          )}
        </Form>
      )}
    </Formik>
  );
};

export default ProductSpecsForm;
